import { useEffect, useState } from 'react';

/**
 * An extension off of the IntersectionObserver API with a polyfill for unsupported browsers.
 * @param callback [IntersectionObserverCallback](https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver#Parameters)
 * @param options [IntersectionObserverInit](https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver#Parameters)
 * @returns [IntersectionObserver](https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver) object or null
 */
export default function useIntersectionObserverCallback(
  callback: IntersectionObserverCallback,
  options?: IntersectionObserverInit
): IntersectionObserver | null {
  const [observer, setObserver] = useState<IntersectionObserver | null>(null);

  useEffect(() => {
    const initIntersectionObserver = async () => {
      const setObserverRef = () => {
        setObserver(new IntersectionObserver(callback, options));
      };

      const isIntersectionObserverSupported =
        'IntersectionObserver' in window &&
        'IntersectionObserverEntry' in window &&
        'intersectionRatio' in window.IntersectionObserverEntry.prototype;

      if (isIntersectionObserverSupported) {
        setObserverRef();
      } else {
        import(/* webpackIgnore: true */ 'intersection-observer').then(() => {
          setObserverRef();
        });
      }
    };
    initIntersectionObserver();
  }, []);

  return observer;
}
